import { Button, Card, CardContent, CardHeader, CardTitle } from "@repo/ui";
import { useAuth } from "@stardeck-customer-apps/project-auth";
import { ListIcon, QrCodeIcon, RefreshCwIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { BankInterestChart } from "@/components/bank-interest-chart";
import { RegistrationTimelineChart } from "@/components/registration-timeline-chart";
import { StatsCard } from "@/components/stats-card";
import { TimelineActivityChart } from "@/components/timeline-activity-chart";
import { useAdminDashboard } from "@/lib/queries/use-registration";
import { useTestAuthApi } from "@/lib/queries/use-test-auth-api";
import AdminNav from "@/pages/admin/admin-nav";

export default function AdminDashboard() {
  useAuth();
  useTestAuthApi();
  const [timeRange, setTimeRange] = useState<
    "today" | "yesterday" | "24h" | "3d" | "7d" | "30d" | "90d" | "expo48"
  >("7d");
  const [granularity, setGranularity] = useState<"daily" | "hourly">("daily");
  const {
    data: dashboard,
    isLoading,
    refetch,
    isFetching,
  } = useAdminDashboard(timeRange, granularity);

  const stats = dashboard?.stats;
  const timelineData = dashboard?.timeline;
  const bankData = dashboard?.banks;
  const timelineLoading = isLoading;

  useEffect(() => {
    document.title = "แดชบอร์ดผู้ดูแล | House & Condo Expo";
  }, []);

  const timeRangeLabel = useMemo(() => {
    const labels: Record<string, string> = {
      today: "วันนี้",
      yesterday: "เมื่อวาน",
      "24h": "24 ชั่วโมง",
      "3d": "3 วัน",
      "7d": "7 วัน",
      "30d": "30 วัน",
      "90d": "90 วัน",
      expo48: "งาน #48",
    };
    return labels[timeRange] || "7 วัน";
  }, [timeRange]);

  // Format data for chart with cumulative values
  const chartData = useMemo(() => {
    if (!timelineData) return [];

    let cumulativePrinted = 0;
    let cumulativeGifts = 0;

    return timelineData.map((point) => {
      cumulativePrinted += point.coupons_printed;
      cumulativeGifts += point.gifts_distributed;

      // Format label based on granularity
      let label: string;
      const date = new Date(point.time_period);

      if (granularity === "hourly") {
        label = date.toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          hour: "numeric",
          hour12: true,
        });
      } else {
        label = date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      }

      return {
        date: label,
        คูปองที่พิมพ์: cumulativePrinted,
        ของขวัญที่แจกจ่าย: cumulativeGifts,
        การลงทะเบียน: point.registrations,
      };
    });
  }, [timelineData, granularity]);

  return (
    <div>
      <AdminNav />
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              refetch();
            }}
            disabled={isFetching}
          >
            <RefreshCwIcon className={`size-4 mr-2 ${isFetching ? "animate-spin" : ""}`} />
            รีเฟรช
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2 ">
            <Link to="/admin/scanner" className="flex-1">
              <Button className="w-full">
                <QrCodeIcon className="size-4" /> สแกน QR
              </Button>
            </Link>
            <Link to="/admin/registrations" className="flex-1">
              <Button variant="outline" className="w-full">
                <ListIcon className="size-4" />
                ดูการลงทะเบียนทั้งหมด
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>ช่วงเวลา</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant={timeRange === "today" ? "default" : "outline"}
                onClick={() => setTimeRange("today")}
              >
                วันนี้
              </Button>
              <Button
                size="sm"
                variant={timeRange === "yesterday" ? "default" : "outline"}
                onClick={() => setTimeRange("yesterday")}
              >
                เมื่อวาน
              </Button>
              <Button
                size="sm"
                variant={timeRange === "expo48" ? "default" : "outline"}
                onClick={() => setTimeRange("expo48")}
              >
                งาน #48
              </Button>
              <Button
                size="sm"
                variant={timeRange === "24h" ? "default" : "outline"}
                onClick={() => setTimeRange("24h")}
              >
                24 ชั่วโมง
              </Button>
              <Button
                size="sm"
                variant={timeRange === "3d" ? "default" : "outline"}
                onClick={() => setTimeRange("3d")}
              >
                3 วัน
              </Button>
              <Button
                size="sm"
                variant={timeRange === "7d" ? "default" : "outline"}
                onClick={() => setTimeRange("7d")}
              >
                7 วัน
              </Button>
              <Button
                size="sm"
                variant={timeRange === "30d" ? "default" : "outline"}
                onClick={() => setTimeRange("30d")}
              >
                30 วัน
              </Button>
              <Button
                size="sm"
                variant={timeRange === "90d" ? "default" : "outline"}
                onClick={() => setTimeRange("90d")}
              >
                90 วัน
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <StatsCard
            title="การลงทะเบียนทั้งหมด"
            value={(stats?.totalRegistrations ?? 0).toLocaleString()}
            isLoading={isLoading}
          />
          <StatsCard
            title={`การลงทะเบียน (${timeRangeLabel})`}
            value={(stats?.todayRegistrations ?? 0).toLocaleString()}
            isLoading={isLoading}
          />
          <StatsCard
            title="ของขวัญที่แจกจ่าย"
            value={(stats?.giftsDistributed ?? 0).toLocaleString()}
            isLoading={isLoading}
          />
          <StatsCard
            title="คูปองพร้อมใช้"
            value={(stats?.couponsReady ?? 0).toLocaleString()}
            isLoading={isLoading}
          />
          <StatsCard
            title="ยอดขายทั้งหมด"
            value={`฿${(stats?.totalSales ?? 0).toLocaleString("en-US")}`}
            isLoading={isLoading}
          />
          <StatsCard
            title={`ยอดขาย (${timeRangeLabel})`}
            value={`฿${(stats?.todaySales ?? 0).toLocaleString("en-US")}`}
            isLoading={isLoading}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <RegistrationTimelineChart
              data={chartData}
              granularity={granularity}
              onGranularityChange={setGranularity}
              isLoading={timelineLoading}
            />
          </div>

          <BankInterestChart data={bankData} isLoading={isLoading} />

          <TimelineActivityChart
            data={chartData}
            granularity={granularity}
            onGranularityChange={setGranularity}
            isLoading={timelineLoading}
          />
        </div>
      </div>
    </div>
  );
}
